import { type CreatePaymentTransactionsRequest } from '@hooks-api/types/apiRequest'
import { AppliedStatus, type AppliedCoupon } from '@hooks-api/types/dataModel/coupon'
import { PaymentMethodType } from '@hooks-api/types/dataModel/payment'
import * as invoiceService from '../core/invoice-service'
import { type FpCampaignCartValues } from '../types'

type GetRequestData = FpCampaignCartValues & {
  appliedCoupons?: AppliedCoupon[]
}

export class CreatePaymentTransactionDTO {
  constructor(private readonly paymentMethod: PaymentMethodType) {
    this.paymentMethod = paymentMethod
  }

  private validateRequiredField<T>(field: T, fieldName: string): NonNullable<T> {
    if (!field) {
      throw new Error(`${fieldName} is required`)
    }
    return field
  }

  getInvoiceRequest(data: GetRequestData) {
    if (this.paymentMethod === PaymentMethodType.OFFLINE_PAYMENT) {
      return {
        receipt_type: undefined,
        refund_statement: data.invoice.refundStatement,
      }
    }
    if (!data.invoice.receiptType) {
      return {
        receipt_type: undefined,
      }
    }
    return {
      receipt_type: data.invoice.receiptType,
      ...invoiceService.resolveInvoiceDetailFields(data.invoice),
      refund_statement: data.invoice.refundStatement,
    }
  }

  getRequest(data: GetRequestData): CreatePaymentTransactionsRequest {
    const appliedShippingMethodId = this.validateRequiredField(data.shipping.appliedShippingMethodId, 'appliedShippingMethodId')
    const billingAddressAddress1 = this.validateRequiredField(data.billing.billingAddressAddress1, 'billingAddressAddress1')
    const billingAddressAddress2 = data.billing.billingAddressAddress2
    const billingAddressCity = data.billing.billingAddressCity
    const billingAddressEmail = this.validateRequiredField(data.billing.billingAddressEmail, 'billingAddressEmail')
    const billingAddressFirstName = this.validateRequiredField(data.billing.billingAddressFirstName, 'billingAddressFirstName')
    const billingAddressPhone = this.validateRequiredField(data.billing.billingAddressPhone, 'billingAddressPhone')
    const billingAddressState = data.billing.billingAddressState
    const billingCountry = this.validateRequiredField(data.billing.billingCountry, 'billingCountry')
    const billingAddressPostcode = data.billing.billingAddressPostcode ?? null
    const orderItems = this.validateRequiredField(data.cart.items, 'orderItems').map(item => ({
      sku: item.sku,
      quantity: item.quantity,
      is_addon: !!item.is_addon,
      project_code: item.project_code,
    }))
    const refundStatement = data.invoice.refundStatement
    const isGiveawayDispatch = data.isGiveawayDispatch
    const manualInputCouponIdsSet = new Set(data.coupon.manualInputCouponIds)
    const appliedCouponIds = data?.appliedCoupons
      ?.filter(coupon => !manualInputCouponIdsSet.has(coupon.coupon_id))
      ?.filter(coupon => coupon.criterions.some(criterion => criterion.applied_status === AppliedStatus.APPLIED))
      .map(coupon => coupon.coupon_id) || []
    const customerNote = data.billing.customerNote
    const manualInputCouponIds = data.coupon.manualInputCouponIds
    const selectedCouponGiveaways = data.isGiveawayDispatch ? [...data.coupon.selectedGiveaways, ...data.coupon.selectedRegularGiveaways] : []

    return {
      applied_shipping_method_id: appliedShippingMethodId,
      billing_country: billingCountry,
      billing_address_first_name: billingAddressFirstName,
      billing_address_phone: billingAddressPhone,
      billing_address_email: billingAddressEmail,
      billing_address_state: billingAddressState,
      billing_address_city: billingAddressCity,
      billing_address_address1: billingAddressAddress1,
      billing_address_address2: billingAddressAddress2 || undefined,
      billing_address_postcode: billingAddressPostcode,
      order_items: orderItems,
      refund_statement: refundStatement,
      selected_coupon_giveaways: selectedCouponGiveaways,
      is_giveaway_dispatch: isGiveawayDispatch === null ? undefined : isGiveawayDispatch,
      applied_coupon_ids: appliedCouponIds,
      customer_note: customerNote,
      manual_input_coupon_ids: manualInputCouponIds,
      ...this.getInvoiceRequest(data),
    }
  }
}
